import { StateCreator } from "zustand";
import { Task } from "@/types/v2";
import { ShopSlice } from "@/stores/shop.slice";
import { ListenersSlice } from "@/stores/listeners.slice";
import { UserSlice } from "@/stores/user.slice";
import { taskRepository } from "@/repositories/task";
import {
  prepareTasks,
  countOpenTasks,
  TaskListFilters,
} from "@/lib/taskSelectors";

export interface TaskSlice {
  tasks: Task[];
  openTaskCount: number;
  taskListFilterPreset: TaskListFilters | null;
  isTasksTabVisible: boolean;
  subscribeTasks: () => void;
  unsubscribeTasks: () => void;
  createTask: (data: Partial<Task>) => Promise<Task>;
  updateTask: (id: string, data: Partial<Task>) => Promise<Task>;
  completeTask: (id: string) => Promise<Task>;
  deleteTask: (id: string) => Promise<void>;
  setTaskListFilterPreset: (preset: TaskListFilters | null) => void;
  setTasksTabVisible: (visible: boolean) => void;
}

const initialSlice: Pick<
  TaskSlice,
  "tasks" | "openTaskCount" | "taskListFilterPreset" | "isTasksTabVisible"
> = {
  tasks: [],
  openTaskCount: 0,
  taskListFilterPreset: null,
  isTasksTabVisible: false,
};

export const createTaskSlice: StateCreator<
  TaskSlice & ShopSlice & ListenersSlice & UserSlice,
  [],
  [],
  TaskSlice
> = (set, get) => ({
  ...initialSlice,
  subscribeTasks: () => {
    const { shopId, user } = get();
    if (!shopId) {
      console.error("Shop ID is missing");
      return;
    }
    if (!user?.uid) {
      console.error("User is missing");
      return;
    }

    const unsubscribe = taskRepository.subscribeAll(
      shopId,
      user.uid,
      (docs) => {
        const tasks = prepareTasks(docs ?? []);
        set({ tasks, openTaskCount: countOpenTasks(tasks) });
      }
    );

    get().setOtherListener("tasks", unsubscribe);
  },
  unsubscribeTasks: () => {
    get().unsubscribeOtherListener("tasks");
  },
  createTask: async (data: Partial<Task>) => {
    const { shopId } = get();
    if (!shopId) {
      throw new Error("Shop ID is missing");
    }
    return taskRepository.create(shopId, data);
  },
  updateTask: async (id: string, data: Partial<Task>) => {
    const { shopId } = get();
    if (!shopId) {
      throw new Error("Shop ID is missing");
    }
    return taskRepository.update(shopId, id, data);
  },
  completeTask: async (id: string) => {
    const { shopId } = get();
    if (!shopId) {
      throw new Error("Shop ID is missing");
    }
    return taskRepository.update(shopId, id, { completed: true });
  },
  deleteTask: async (id: string) => {
    const { shopId } = get();
    if (!shopId) {
      throw new Error("Shop ID is missing");
    }
    await taskRepository.delete(shopId, id);
  },
  setTaskListFilterPreset: (preset: TaskListFilters | null) => {
    set({ taskListFilterPreset: preset });
  },
  setTasksTabVisible: (visible: boolean) => {
    set({ isTasksTabVisible: visible });
  },
});
